import { useState, useEffect, useCallback } from 'react'
import { CreditCard, Check, X, TrendingUp, Calendar, Download, AlertCircle, Receipt, Sparkles, Database } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import { useConfirmDialog } from '@/components/ui/confirm-dialog'
import { PageHeader, FluentCard, StatCard } from '@/components/layout/page-header'
import {
  billingService,
  type SubscriptionPlan,
  type Subscription,
  type UsageStats,
  type Invoice,
  type PaymentMethod,
  type BillingAddress,
} from '@/lib/api/services/billing'

export default function Billing() {
  const { toast } = useToast()
  const { confirm, ConfirmDialogComponent } = useConfirmDialog()
  const [loading, setLoading] = useState(true)
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month')
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [billingAddress, setBillingAddress] = useState<BillingAddress | null>(null)

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true)
      const [plansData, subscriptionData, usageData, invoicesData, paymentMethodsData, addressData] =
        await Promise.all([
          billingService.getPlans(),
          billingService.getSubscription(),
          billingService.getUsage(),
          billingService.getInvoices({ top: 10 }),
          billingService.getPaymentMethods(),
          billingService.getBillingAddress(),
        ])

      setPlans(plansData)
      setSubscription(subscriptionData)
      setUsage(usageData)
      setInvoices(invoicesData.value || [])
      setPaymentMethods(paymentMethodsData)
      setBillingAddress(addressData)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to load billing information',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadBillingData()
  }, [loadBillingData])

  const handleUpgrade = async (planId: string) => {
    try {
      const { sessionUrl } = await billingService.createCheckoutSession(planId, billingInterval)
      window.location.href = sessionUrl
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to start checkout process',
        variant: 'destructive',
      })
    }
  }

  const handleCancelSubscription = async () => {
    const confirmed = await confirm({
      title: 'Cancel Subscription',
      description: 'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.',
      confirmLabel: 'Cancel Subscription',
      variant: 'destructive',
    })
    if (!confirmed) return

    try {
      await billingService.cancelSubscription()
      toast({
        title: 'Subscription cancelled',
        description: 'Your subscription will remain active until the end of the billing period.',
      })
      loadBillingData()
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel subscription',
        variant: 'destructive',
      })
    }
  }

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const blob = await billingService.downloadInvoice(invoiceId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `invoice-${invoiceId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to download invoice',
        variant: 'destructive',
      })
    }
  }

  const handleManageBilling = async () => {
    try {
      const { portalUrl } = await billingService.getCustomerPortalUrl()
      window.location.href = portalUrl
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to open billing portal',
        variant: 'destructive',
      })
    }
  }

  const getPrice = (plan: SubscriptionPlan) => {
    const basePrice = plan.price
    if (billingInterval === 'year') {
      return Math.floor(basePrice * 10) // 2 months free
    }
    return basePrice
  }

  const currentPlanId = subscription?.planId

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full rounded-none" />
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-none" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {ConfirmDialogComponent}
      <PageHeader
        title="Billing & Subscription"
        description="Manage your subscription and billing information"
      />

      {subscription && (
        <FluentCard className="border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/30">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white">Next billing date</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your next payment of <span className="font-semibold">${subscription.price.toFixed(2)}</span> will be processed on{' '}
                <span className="font-semibold">{new Date(subscription.currentPeriodEnd).toLocaleDateString()}</span>.
              </p>
            </div>
          </div>
        </FluentCard>
      )}

      <Tabs defaultValue="overview" className="space-y-6">
        <div className="bg-white dark:bg-gray-900/50 rounded-2xl p-1.5 inline-flex border border-gray-100 dark:border-gray-800" style={{ boxShadow: '0 0 2px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)' }}>
          <TabsList className="bg-transparent border-0 gap-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">Overview</TabsTrigger>
            <TabsTrigger value="plans" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">Plans</TabsTrigger>
            <TabsTrigger value="usage" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">Usage</TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">Invoices</TabsTrigger>
            <TabsTrigger value="payment" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">Payment Method</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Current Plan"
              value={subscription?.planName || 'Free'}
              icon={CreditCard}
              color="from-indigo-400 to-violet-600"
            />
            <StatCard
              label="Templates Used"
              value={usage?.templates.used || 0}
              icon={TrendingUp}
              color="from-violet-400 to-purple-600"
            />
            <StatCard
              label="AI Generations"
              value={usage?.aiGenerations.used.toLocaleString() || '0'}
              icon={Sparkles}
              color="from-amber-400 to-orange-600"
            />
            <StatCard
              label="Storage Used"
              value={`${usage?.storage.used || 0} GB`}
              icon={Database}
              color="from-emerald-400 to-green-600"
            />
          </div>

          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-gray-400 to-gray-600">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Manage Billing</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Update payment method, view invoices, and more
                  </p>
                </div>
                <Button onClick={handleManageBilling} className="rounded-full px-6">Open Portal</Button>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Team Members</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Currently using {usage?.teamMembers.used || 0} of{' '}
                    {usage?.teamMembers.limit || 0} seats
                  </p>
                </div>
                <Button variant="outline" className="rounded-full px-6">Add Members</Button>
              </div>
            </div>
          </FluentCard>
        </TabsContent>

        <TabsContent value="plans" className="space-y-6">
          <FluentCard>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-600">
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Available Plans</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Choose the plan that best fits your needs</p>
                </div>
              </div>
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                <button
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    billingInterval === 'month'
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                  onClick={() => setBillingInterval('month')}
                >
                  Monthly
                </button>
                <button
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                    billingInterval === 'year'
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                  onClick={() => setBillingInterval('year')}
                >
                  Yearly
                  <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                    Save 17%
                  </span>
                </button>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border-2 p-6 transition-all ${
                    plan.id === currentPlanId
                      ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white">{plan.name}</h4>
                    <div className="flex gap-2">
                      {plan.recommended && (
                        <span className="px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
                          Recommended
                        </span>
                      )}
                      {plan.id === currentPlanId && (
                        <span className="px-3 py-1 text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">${getPrice(plan)}</span>
                    <span className="text-gray-500 dark:text-gray-400">/{billingInterval}</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="p-1 rounded-full bg-green-100 dark:bg-green-900/30">
                          <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                        </div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                    {plan.limitations.map((limitation, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="p-1 rounded-full bg-gray-100 dark:bg-gray-800">
                          <X className="h-3 w-3 text-gray-400" />
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{limitation}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    className={`w-full rounded-full ${
                      plan.id === currentPlanId
                        ? ''
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                    variant={plan.id === currentPlanId ? 'outline' : 'default'}
                    disabled={plan.id === currentPlanId}
                    onClick={() => handleUpgrade(plan.id)}
                  >
                    {plan.id === currentPlanId ? 'Current Plan' : 'Upgrade'}
                  </Button>
                </div>
              ))}
            </div>
          </FluentCard>
        </TabsContent>

        <TabsContent value="usage" className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Details</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Track your resource usage for the current billing period</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900 dark:text-white">Templates Created</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {usage?.templates.used || 0}{' '}
                    {usage?.templates.limit === -1 ? '(Unlimited)' : `/ ${usage?.templates.limit}`}
                  </span>
                </div>
                <Progress
                  value={
                    usage?.templates.limit === -1
                      ? 100
                      : ((usage?.templates.used || 0) / (usage?.templates.limit || 1)) * 100
                  }
                  className="h-2"
                />
              </div>

              <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900 dark:text-white">AI Generations</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {usage?.aiGenerations.used.toLocaleString() || 0} /{' '}
                    {usage?.aiGenerations.limit.toLocaleString() || 0}
                  </span>
                </div>
                <Progress
                  value={
                    ((usage?.aiGenerations.used || 0) / (usage?.aiGenerations.limit || 1)) * 100
                  }
                  className="h-2"
                />
              </div>

              <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900 dark:text-white">Storage</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {usage?.storage.used || 0} GB / {usage?.storage.limit || 0} GB
                  </span>
                </div>
                <Progress
                  value={((usage?.storage.used || 0) / (usage?.storage.limit || 1)) * 100}
                  className="h-2"
                />
              </div>

              <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900 dark:text-white">Team Members</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {usage?.teamMembers.used || 0} / {usage?.teamMembers.limit || 0}
                  </span>
                </div>
                <Progress
                  value={((usage?.teamMembers.used || 0) / (usage?.teamMembers.limit || 1)) * 100}
                  className="h-2"
                />
              </div>
            </div>
          </FluentCard>
        </TabsContent>

        <TabsContent value="invoices" className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                <Receipt className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Invoice History</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Download your past invoices and receipts</p>
              </div>
            </div>
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No invoices found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2.5 rounded-xl bg-white dark:bg-gray-700">
                        <Calendar className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{invoice.invoiceNumber}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(invoice.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {invoice.currency} {invoice.amount.toFixed(2)}
                        </p>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {invoice.status}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDownloadInvoice(invoice.id)}
                        className="p-2 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors"
                      >
                        <Download className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </FluentCard>
        </TabsContent>

        <TabsContent value="payment" className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Method</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage your payment methods and billing details</p>
              </div>
            </div>
            <div className="space-y-4">
              {paymentMethods.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No payment methods on file</p>
                </div>
              ) : (
                paymentMethods.map((method) => (
                  <div key={method.id} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-16 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-white font-bold text-xs">
                          {method.brand?.toUpperCase() || 'CARD'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {method.brand} ending in {method.last4}
                          </p>
                          {method.expiryMonth && method.expiryYear && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Expires {method.expiryMonth}/{method.expiryYear}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {method.isDefault && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                            Default
                          </span>
                        )}
                        <Button variant="outline" size="sm" onClick={handleManageBilling} className="rounded-lg">
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <Button variant="outline" className="w-full rounded-xl" onClick={handleManageBilling}>
                <CreditCard className="mr-2 h-4 w-4" />
                Add Payment Method
              </Button>

              {billingAddress && (
                <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Billing Address</h4>
                  <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 space-y-3">
                    {billingAddress.company && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Company</p>
                        <p className="text-gray-900 dark:text-white">{billingAddress.company}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Address</p>
                      <p className="text-gray-900 dark:text-white">{billingAddress.line1}</p>
                      {billingAddress.line2 && <p className="text-gray-900 dark:text-white">{billingAddress.line2}</p>}
                      <p className="text-gray-900 dark:text-white">
                        {billingAddress.city}
                        {billingAddress.state && `, ${billingAddress.state}`}{' '}
                        {billingAddress.postalCode}
                      </p>
                      <p className="text-gray-900 dark:text-white">{billingAddress.country}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      onClick={handleManageBilling}
                    >
                      Edit Address
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </FluentCard>

          <FluentCard className="border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-red-100 dark:bg-red-900/30">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">Danger Zone</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Once you cancel your subscription, you will lose access to premium features at the end of your billing period.
            </p>
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
              onClick={handleCancelSubscription}
            >
              Cancel Subscription
            </Button>
          </FluentCard>
        </TabsContent>
      </Tabs>
    </div>
  )
}
